

























import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { FOCUS_SEARCHBAR, ENTER, CLEAR_ASSOCIATION_MESSAGE } from "@/utils/constants";
import EventBus from "@/utils/event-bus";
import * as modals from "@/utils/modals";
import { Settings } from "@/models";

export interface EnteredData {
  isScanned: boolean;
  raw: string;
  type: string;
  barcode: string | null;
}

let autoSubmitTimer: number;

@Component
export default class PatientInfo extends Vue {
  @Prop() readonly settings!: Settings;
  @Prop() readonly placeholder?: string;

  // we usually expect a scanner as the input device
  isScanned = true;
  raw = "";
  lastInput?: number;

  created() {
    // Don't focus the search bar automatically for mobile apps because the user will most likely be using the camera
    // to scan barcodes and if the input is focused, it will open the phone's keyboard
    if (!this.$nativeApp.isMobile) {
      EventBus.$on(FOCUS_SEARCHBAR, this.focus);
    }
  }

  destoryed() {
    EventBus.$off(FOCUS_SEARCHBAR, this.focus);
  }

  mounted() {
    if (!this.$nativeApp.isMobile) {
      // If this element is mounted (or re-mounted), immediately focus
      EventBus.$emit(FOCUS_SEARCHBAR);

      // This allows the user to use tab to navigate between buttons but clicking on other elements (e.g. the
      // background), will send focus back to the search bar
      document.addEventListener("click", this.keepFocus);
    }
  }

  unmounted() {
    if (!this.$nativeApp.isMobile) {
      document.removeEventListener("click", this.keepFocus);
    }
  }

  // If the user has clicked off of the search bar, put focus back so that if the user clicked on a link or on the
  // page's background, they can still use the barcode scanner without having to manually click back into the input
  keepFocus() {
    // If a modal is open, allow the focus to leave so that the user can fill out the modal (e.g. the "Other" reason
    // for manual entry)
    if (modals.isAnyModalOpen()) return;

    EventBus.$emit(FOCUS_SEARCHBAR);
  }

  scanBarcode(barcode: string) {
    // If there isn't a value, nothing was scanned so there's nothing to do
    if (!barcode) return;

    this.isScanned = true;
    this.raw = barcode;
    this.checkInput();
  }

  submit() {
    // If the user hasn't entered anything, just reset focus and then don't bother doing anything
    if (!this.raw) {
      EventBus.$emit(FOCUS_SEARCHBAR);
      return;
    }

    // Open the Manual Entry Reason modal, if the user clicks OK on the modal, it will submit the barcode
    this.isScanned = false;
    this.checkInput();
  }

  onInput(event: KeyboardEvent) {
    console.log(
      "using",
      this.settings.autoSubmitEnabled,
      this.settings.autoSubmitChars,
      this.settings.autoSubmitIdleTimeout
    );
    EventBus.$emit(CLEAR_ASSOCIATION_MESSAGE);
    if (this.raw === "") {
      this.lastInput = undefined;
      this.isScanned = true;
      return;
    }
    const now = Date.now();
    console.log(this.isScanned, this.raw, this.raw.length);
    // set timestamp, flag as potential scan and return on first input
    if (!this.lastInput) {
      this.lastInput = now;
      this.isScanned = true;
      return;
    }
    console.log(now - this.lastInput, now - this.lastInput <= this.settings.scannerSensitivity);
    // clear pending timeout
    clearTimeout(autoSubmitTimer);
    autoSubmitTimer = 0;
    // chain isScanned
    this.isScanned = now - this.lastInput <= this.settings.scannerSensitivity && this.isScanned;
    // still considered a scan && more than X chars, trigger timeout
    if (this.isScanned && this.settings.autoSubmitEnabled && this.raw.length > this.settings.autoSubmitChars) {
      autoSubmitTimer = window.setTimeout(() => {
        console.log("triggered auto submit");
        this.checkInput();
      }, this.settings.autoSubmitIdleTimeout);
    }
    // update lastInput on every keystroke
    this.lastInput = now;
    if (event.keyCode === ENTER) {
      EventBus.$emit(CLEAR_ASSOCIATION_MESSAGE);
      // if we get an "ENTER" by a scanner immediately call check input
      if (this.isScanned) {
        clearTimeout(autoSubmitTimer);
        autoSubmitTimer = 0;
        this.checkInput();
      } else {
        // otherwise handle enter key press submitting a "manual" (not scanned) barcode
        this.isScanned = false;
        this.checkInput();
      }
    }
  }

  checkInput() {
    const data: EnteredData = {
      type: "",
      barcode: "",
      raw: this.raw,
      isScanned: this.isScanned
    };
    this.$emit("barcode-entered", data);
    this.reset();
    this.blur();
  }

  reset() {
    this.isScanned = true;
    this.lastInput = undefined;
    this.raw = "";
  }

  focus() {
    if (!this.$refs.input) {
      // not yet mounted ? ignore !
      return;
    }
    (this.$refs.input as HTMLInputElement)?.focus();
  }

  blur() {
    if (!this.$refs.input) {
      // not yet mounted ? ignore !
      return;
    }
    (this.$refs.input as HTMLInputElement).blur();
  }
}
